import React, { useState, useCallback, useEffect } from "react";
import { Badge, Tooltip, Text, Box } from "@chakra-ui/react";
import { DarwinHeader } from "build-ts/common";
import { getAssignedPrompts } from "../../services/NewDarwinEvaServices";
import Table, { TableColumn, TablePagination } from "../common/Table";
import { epochConverterWithTimeZone } from "../../utils/dateUtils";
import { useUser } from "context/UserContext";

interface PromptSelectionTableProps {
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedItems?: Set<string>;
  selectedAgentId?: string;
}

const PromptSelectionTable: React.FC<PromptSelectionTableProps> = ({
  onSelectionChange,
  selectedItems = new Set(),
  selectedAgentId,
}) => {
  const { defaultTimeZone } = useUser();

  // State for data
  const [prompts, setPrompts] = useState<DarwinHeader[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mock pagination for the table (since API doesn't support pagination)
  const pagination: TablePagination = {
    total: prompts.length,
    offset: 0,
    has_more: false,
  };

  // Fetch prompts for the selected agent
  const fetchPrompts = useCallback(async (agentId: string) => {
    if (!agentId) {
      setPrompts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await getAssignedPrompts(agentId);

      // Handle different response formats
      if (Array.isArray(result)) {
        setPrompts(result);
      } else if (result && result.prompts && Array.isArray(result.prompts)) {
        setPrompts(result.prompts);
      } else {
        setPrompts([]);
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load prompts when selectedAgentId changes
  useEffect(() => {
    if (selectedAgentId) {
      fetchPrompts(selectedAgentId);
    } else {
      setPrompts([]);
      setError(null);
    }
  }, [selectedAgentId, fetchPrompts]);

  const handleSelectionChange = useCallback((newSelectedPrompts: Set<string>) => {
    onSelectionChange?.(newSelectedPrompts);
  }, [onSelectionChange]);

  // Table configuration
  const tableColumns: TableColumn<DarwinHeader>[] = [
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (prompt) => (
        <Tooltip label={prompt.description || "No description available"} placement="top">
          <Text fontSize="sm" color="gray.600" noOfLines={2} wordBreak="break-word" mb={0}>
            {prompt.description || "No description available"}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "version",
      label: "Version",
      className: "eva-version-column",
      render: (prompt) => (
        <Badge colorScheme="blue" variant="outline">
          v{prompt.version || "N/A"}
        </Badge>
      ),
    },
    {
      key: "is_active",
      label: "Status",
      className: "eva-status-column",
      render: (prompt) => (
        <Badge colorScheme={prompt.is_active ? "green" : "red"} variant="solid">
          {prompt.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "created_date",
      label: "Created",
      className: "eva-date-column",
      render: (prompt) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(prompt.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date",
      label: "Modified",
      className: "eva-date-column",
      render: (prompt) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(prompt.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    }
  ];

  // Show message when no agent is selected
  if (!selectedAgentId) {
    return (
      <Box p={4}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minH="300px"
          bg="gray.50"
          borderRadius="md"
        >
          <Text fontSize="lg" color="gray.500" fontWeight="medium">
            Please select a Darwin agent first to view assigned prompts
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Table
        data={prompts}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        selectable={true}
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
        getItemId={(prompt) => prompt.id || ""}
        singleSelection={true}
        emptyMessage="No prompts found for this agent"
        emptySubMessage="This agent doesn't have any assigned prompts"
      />
    </Box>
  );
};

export default PromptSelectionTable;
